import { Controller } from "@hotwired/stimulus"

// Generic two-or-more-mode view toggle for listing pages. The
// controller doesn't bake any layout decisions in itself — each
// button declares its mode name (`data-mode`) and the exact grid
// classes that should be applied when the mode is active
// (`data-grid-classes`). The wrapping element provides a storage
// key and default mode via Stimulus values. localStorage persists
// the user's choice across page loads.
//
// Usage:
//
//   <div data-controller="view-mode"
//        data-view-mode-storage-key-value="discovery-podcasts-view-mode"
//        data-view-mode-default-mode-value="comfortable">
//     <button data-action="click->view-mode#select"
//             data-view-mode-target="button"
//             data-mode="comfortable"
//             data-grid-classes="grid grid-cols-1 md:grid-cols-3 gap-6">…</button>
//     <button data-action="click->view-mode#select"
//             data-view-mode-target="button"
//             data-mode="compact"
//             data-grid-classes="grid grid-cols-2 md:grid-cols-5 gap-3">…</button>
//     <div data-view-mode-target="grid" class="grid grid-cols-1 md:grid-cols-3 gap-6">
//       …cards…
//     </div>
//   </div>
//
// The grid container's initial classes should match the default
// mode's `data-grid-classes` so there's no visible re-flow when
// the controller boots.
export default class extends Controller {
  static targets = ["grid", "button"]
  static values = { storageKey: String, defaultMode: String }

  connect() {
    let saved = ""
    try {
      saved = localStorage.getItem(this.storageKeyValue) || ""
    } catch (_e) {
      // localStorage blocked (private mode, quota, etc.) — fall
      // back to the default mode for this session.
    }

    const mode =
      saved ||
      this.defaultModeValue ||
      this.buttonTargets[0]?.dataset.mode ||
      ""
    this.apply(mode)
  }

  select(event) {
    const mode = event.currentTarget.dataset.mode
    if (!mode) return
    this.apply(mode)
    try {
      localStorage.setItem(this.storageKeyValue, mode)
    } catch (_e) {
      // Non-fatal — preference simply doesn't persist this session.
    }
  }

  apply(mode) {
    const btn =
      this.buttonTargets.find((b) => b.dataset.mode === mode) ||
      this.buttonTargets[0]
    if (!btn) return

    const classes = btn.dataset.gridClasses
    if (classes && this.hasGridTarget) {
      this.gridTarget.className = classes
    }

    // Active-state styling. We toggle a small set of utility
    // classes rather than swapping the entire className so the
    // base styling (size, radius, transitions) declared in the
    // template stays intact.
    this.buttonTargets.forEach((b) => {
      const active = b === btn
      b.setAttribute("aria-pressed", String(active))
      b.classList.toggle("bg-primary-fixed", active)
      b.classList.toggle("text-on-primary-fixed", active)
      b.classList.toggle("text-on-surface-variant", !active)
      b.classList.toggle("hover:bg-surface-container-high", !active)
    })
  }
}
